import { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Plus, Pencil, Trash2, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CoverCropDialog } from '@/components/ui/CoverCropDialog';
import { relativeTime } from '@/lib/relativeTime';
import {
  fetchAllUpdatesAdmin, createUpdate, updateUpdate, deleteUpdate,
} from '@/services/updatesService';
import { ProductUpdate } from '@/types';

interface UpdateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: ProductUpdate | null;
  onSaved: () => void;
}

const UpdateFormDialog: React.FC<UpdateFormDialogProps> = ({ open, onOpenChange, existing, onSaved }) => {
  const [title, setTitle] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [published, setPublished] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cropSource, setCropSource] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(existing?.title ?? '');
    setBodyMd(existing?.bodyMd ?? '');
    setYoutubeUrl(existing?.youtubeUrl ?? '');
    setPublished(existing?.published ?? false);
    setCoverFile(null);
    setCoverPreview(existing?.coverImageUrl ?? null);
    setError(null);
  }, [open, existing]);

  const pickFile = (f: File) => {
    if (f.type === 'image/gif') {
      // GIFs skip the pixel crop (canvas would freeze the animation);
      // display-side object-cover center-crops them into 16:9 instead.
      setCoverFile(f);
      setCoverPreview(URL.createObjectURL(f));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    // Static images must go through the 16:9 crop before they become the cover.
    setCropSource(f);
  };

  const handleCropped = (croppedFile: File, previewUrl: string) => {
    setCoverFile(croppedFile);
    setCoverPreview(previewUrl);
    setCropSource(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCropCancel = () => {
    setCropSource(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearFile = () => {
    setCoverFile(null);
    setCoverPreview(existing?.coverImageUrl ?? null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Titel darf nicht leer sein.'); return; }
    setSaving(true);
    setError(null);
    try {
      if (existing) {
        await updateUpdate(existing.id, {
          title, bodyMd, youtubeUrl: youtubeUrl || null,
          coverFile, published, publishedAt: existing.publishedAt,
        });
      } else {
        await createUpdate({ title, bodyMd, youtubeUrl: youtubeUrl || null, coverFile, publish: published });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Scrolling lives in an inner child: making the transformed (translate-centered),
          rounded DialogContent itself the scroller causes white compositing artifacts
          at the edges during fast momentum scrolls (same pattern as UpdateDetailModal). */}
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
          <DialogTitle>{existing ? 'Update bearbeiten' : 'Neues Update'}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto overscroll-contain px-6 pb-4 space-y-4">
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z.B. Neuer Grundriss-Editor" />
          </div>

          <div className="space-y-2">
            <Label>Cover-Bild / GIF</Label>
            {coverPreview ? (
              <div className="relative w-full max-w-sm aspect-video rounded-lg overflow-hidden border border-border bg-muted">
                <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={clearFile}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-background/80 border border-border flex items-center justify-center hover:bg-destructive/10 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full max-w-sm aspect-video rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-primary/50 transition-colors"
              >
                <ImageIcon className="w-6 h-6" />
                <span className="text-sm">Bild oder GIF hochladen</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) pickFile(f); }}
            />
            {coverFile?.type === 'image/gif' && (
              <p className="text-xs text-muted-foreground">
                GIF wird mittig ins 16:9-Format eingepasst, die Animation bleibt erhalten.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Beschreibung (Markdown)</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Textarea
                value={bodyMd}
                onChange={(e) => setBodyMd(e.target.value)}
                rows={10}
                placeholder="Was ist neu? **Fett**, Listen mit -, [Links](https://…) möglich."
                className="font-mono text-sm"
              />
              <div className="rounded-lg border border-border p-3 overflow-y-auto max-h-[260px] text-sm text-foreground space-y-2 [&_h2]:text-lg [&_h2]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:underline [&_strong]:font-semibold">
                {bodyMd.trim() ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{bodyMd}</ReactMarkdown>
                ) : (
                  <span className="text-muted-foreground">Vorschau…</span>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground">YouTube-Link (optional, für später)</Label>
            <Input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtube.com/watch?v=…" />
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={published} onCheckedChange={setPublished} id="publish-switch" />
            <Label htmlFor="publish-switch" className="cursor-pointer">Veröffentlichen (für alle User sichtbar)</Label>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter className="shrink-0 px-6 pb-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {existing ? 'Speichern' : 'Anlegen'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <CoverCropDialog file={cropSource} onCancel={handleCropCancel} onCropped={handleCropped} />
    </Dialog>
  );
};

export const AdminUpdatesView: React.FC = () => {
  const [updates, setUpdates] = useState<ProductUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProductUpdate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductUpdate | null>(null);

  const load = () => {
    setLoading(true);
    fetchAllUpdatesAdmin()
      .then(setUpdates)
      .catch((err) => console.error('[Updates] admin load', err))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (u: ProductUpdate) => { setEditing(u); setFormOpen(true); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUpdate(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      console.error('[Updates] delete', err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-foreground">Produkt-Updates</h2>
        <Button onClick={openNew} className="flex items-center gap-2">
          <Plus className="w-4 h-4" /> Neues Update
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : updates.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Noch keine Updates. Lege das erste an.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {updates.map((u) => (
            <Card key={u.id}>
              <CardContent className="p-4 flex items-center gap-4">
                {u.coverImageUrl ? (
                  <img src={u.coverImageUrl} alt="" className="w-16 h-16 rounded-md object-cover border border-border flex-shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-md bg-muted border border-border flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-foreground truncate">{u.title}</p>
                    <Badge variant={u.published ? 'default' : 'secondary'}>{u.published ? 'Live' : 'Entwurf'}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {u.published && u.publishedAt ? `Veröffentlicht ${relativeTime(u.publishedAt)}` : `Erstellt ${relativeTime(u.createdAt)}`}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(u)} aria-label="Bearbeiten"><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(u)} aria-label="Löschen"><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <UpdateFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        existing={editing}
        onSaved={() => { setFormOpen(false); load(); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              „{deleteTarget?.title}" wird dauerhaft gelöscht. Das kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
